import { emailFraudSearch } from '@/reports/fraud/email/emailFraudSearch';
import { unclaimedMoneySearch } from '@/unclaimedMoney/index';
import { emailSearch } from '../SearchEmailForm/emailSearch';
import { ipSearch } from '../SearchIpFraudForms/ipSearch';
import { personSearch } from '../SearchPersonForm/personSearch';
import { phoneSearch } from '../SearchPhoneForm/phoneSearch';
import { addressSearch } from '../SearchPropertyForm/propertySearch';
import { urlSearch } from '../SearchURLFraudForm/urlSearch';
import { usernameSearch } from '../SearchUsernameForm/usernameSearch';
import { ancestorSearch } from '../ancestor/ancestorSearch';
import { contactSearch } from '../contact/contactSearch';
import obituarySearch from '../obituary/ObituarySearch';
import { vehicleSearch } from '../vehicle/vehicleSearch';

interface Params {
  queryParams: URLSearchParams;
}

function getEmailPermalink({ queryParams }: Params) {
  const email = queryParams.get('email') || '';
  return emailSearch({ email });
}

function getUsernamePermalink({ queryParams }: Params) {
  const username = queryParams.get('username') || '';
  return usernameSearch({ username });
}

function getPhonePermalink({ queryParams }: Params) {
  const phone =
    queryParams.get('phone') || queryParams.get('phone_number') || '';
  const report_flags = queryParams.get('report_flags') || '';

  return phoneSearch({ phone, report_flags });
}

function getPersonPermalink({ queryParams }: Params) {
  const bvid = queryParams.get('bvid') || '';
  const showPartialAddresses =
    queryParams.get('showPartialAddresses') === 'true';
  return personSearch({ bvid, showPartialAddresses });
}

function getPropertyPermalink({ queryParams }: Params) {
  const address = queryParams.get('address') || '';
  const city = queryParams.get('city') || '';
  const state = queryParams.get('state') || '';
  const zipcode = queryParams.get('zipcode') || '';
  const zip_code = queryParams.get('zip_code') || '';
  const property_id = queryParams.get('property_id') || '';
  const report_flags = queryParams.get('report_flags') || '';
  return addressSearch({
    address,
    city,
    state,
    zipcode,
    zip_code,
    property_id,
    report_flags,
  });
}

function getVehiclePermalink({ queryParams }: Params) {
  const vin = queryParams.get('vin') || '';
  const licensePlate = queryParams.get('license_plate') || '';
  const state = queryParams.get('state') || '';
  const make = queryParams.get('make') || '';
  const model = queryParams.get('model') || '';
  const year = queryParams.get('year') || '';
  const trim = queryParams.get('trim') || '';
  return vehicleSearch({
    vin,
    licensePlate,
    state,
    make,
    model,
    year,
    trim,
  });
}

function getObituaryPermalink({ queryParams }: Params) {
  const id = queryParams.get('id') || '';
  return obituarySearch({ id });
}

function getContactPermalink({ queryParams }: Params) {
  const contact_id = queryParams.get('contact_id') || '';

  return contactSearch({ contact_id });
}

function getUnclaimedMoneyPermalink({ queryParams }: Params) {
  const state = queryParams.get('state') || '';
  const first_name = queryParams.get('first_name') || '';
  const last_name = queryParams.get('last_name') || '';
  const multi_state_search = queryParams.get('multi_state_search') === 'true';

  return unclaimedMoneySearch({
    first_name,
    last_name,
    state,
    multi_state_search,
  });
}

function getEmailFraudPermalink({ queryParams }: Params) {
  const email = queryParams.get('email') || '';
  return emailFraudSearch({ email });
}

function getPhoneFraudPermalink({ queryParams }: Params) {
  const phone = queryParams.get('phone') || '';
  return phoneSearch({ phone });
}

function getAncestorPermalink({ queryParams }: Params) {
  const first_name = queryParams.get('first_name') || '';
  const last_name = queryParams.get('last_name') || '';
  const city = queryParams.get('city') || '';
  const state = queryParams.get('state') || '';
  const country = queryParams.get('country') || '';
  const date_of_death = queryParams.get('date_of_death') || '';
  const date_of_birth = queryParams.get('date_of_birth') || '';
  const family_search_id = queryParams.get('family_search_id') || '';
  const ip_address = queryParams.get('ip_address') || '';

  return ancestorSearch({
    first_name,
    last_name,
    city,
    state,
    country,
    date_of_birth,
    date_of_death,
    family_search_id,
    ip_address,
  });
}

function getIpFraudPermalink({ queryParams }: Params) {
  const ipAddress = queryParams.get('ip_address') || '';
  return ipSearch({ ipAddress });
}

function getUrlFraudPermalink({ queryParams }: Params) {
  const url = queryParams.get('url') || '';
  return urlSearch({ url });
}

export const mapReportTypesToReportParams = {
  detailed_person_report: 'person',
  reverse_phone_report: 'phone',
  property_report: 'property',
  social_network_report: 'email',
  username_report: 'username',
  sex_offender_report: 'sex-offender',
  vehicle_report: 'vehicle',
  contact_report: 'contact',
  obituary_report: 'obituary',
  unclaimed_money_report: 'unclaimed-money',
  email_fraud_report: 'email-fraud',
  phone_fraud_report: 'phone-fraud',
  ancestry_report: 'ancestor',
  ip_fraud_report: 'ip-fraud',
  url_fraud_report: 'url-fraud',
};

export const mapReportParamsToReportTypes: { [key: string]: string } = {
  person: 'detailed_person_report',
  phone: 'reverse_phone_report',
  property: 'property_report',
  email: 'social_network_report',
  username: 'username_report',
  vehicle: 'vehicle_search',
  obituary: 'obituary_search',
  contact: 'contact_report',
  ancestor: 'ancestry_report',
  'unclaimed-money': 'unclaimed_money_report',
  'email-fraud': 'email_fraud_report',
  'phone-fraud': 'phone_fraud_report',
  'ip-fraud': 'ip_fraud_report',
  'url-fraud': 'url_fraud_report',
};

export const reportGetters = {
  person: getPersonPermalink,
  phone: getPhonePermalink,
  property: getPropertyPermalink,
  email: getEmailPermalink,
  username: getUsernamePermalink,
  vehicle: getVehiclePermalink,
  obituary: getObituaryPermalink,
  contact: getContactPermalink,
  ancestor: getAncestorPermalink,
  'unclaimed-money': getUnclaimedMoneyPermalink,
  'email-fraud': getEmailFraudPermalink,
  'phone-fraud': getPhoneFraudPermalink,
  'ip-fraud': getIpFraudPermalink,
  'url-fraud': getUrlFraudPermalink,
};
